export type ActivityStatus = 'PENDING' | 'IN_PROGRESS' | 'DELAYED' | 'CRITICAL' | 'BLOCKED' | 'DONE';
export type RiskKind = 'RISK' | 'ISSUE';
export type RiskCriticality = 'LOW' | 'MEDIUM' | 'HIGH';
export type RiskStatus = 'OPEN' | 'CLOSED';
export type AccessStatus = 'OK' | 'NA' | 'PENDING';

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
    PENDING: 'Pendente',
    IN_PROGRESS: 'Em curso',
    DELAYED: 'Em curso com atraso',
    CRITICAL: 'Atraso crítico',
    BLOCKED: 'Bloqueado',
    DONE: 'Concluído',
};

// Mesmas cores do relatório de seguimento
export const ACTIVITY_STATUS_COLORS: Record<ActivityStatus, string> = {
    PENDING: '#9e9e9e',
    IN_PROGRESS: '#2e7d32',
    DELAYED: '#f9a825',
    CRITICAL: '#c62828',
    BLOCKED: '#6a1b9a',
    DONE: '#1565c0',
};

export const CRITICALITY_LABELS: Record<RiskCriticality, string> = { LOW: 'Baixa', MEDIUM: 'Média', HIGH: 'Alta' };
export const CRITICALITY_COLORS: Record<RiskCriticality, string> = { LOW: '#2e7d32', MEDIUM: '#f9a825', HIGH: '#c62828' };
export const RISK_STATUS_LABELS: Record<RiskStatus, string> = { OPEN: 'Aberto', CLOSED: 'Fechado' };

export interface ActivityUpdate {
    id: number;
    activity_id: number;
    report_date: string;
    highlights: string | null;
    next_steps: string | null;
    status: ActivityStatus | null;
    author_id: number | null;
    updated_at: string | null;
}

export interface ActivityNode {
    tasks?: { total: number; open: number; late: number }; // tarefas vinculadas (inclui subatividades)
    id: number;
    workstream_id: number;
    parent_id: number | null;
    name: string;
    order: number;
    depth: number;
    planned_start: string | null;
    planned_end: string | null;
    actual_start: string | null;
    actual_end: string | null;
    progress: number;
    weight: number;
    status: ActivityStatus; // estado exibido (manual ou sugerido)
    manual_status: ActivityStatus; // estado informado; PENDING = usar o sugerido
    status_label: string;
    suggested_status: ActivityStatus;
    milestone_label: string | null;
    responsible: string | null; // nomes (exibição)
    responsible_ids: number[]; // pessoas do time
    progress_real: number;
    progress_expected: number | null;
    risk_ids: number[];
    risk_codes: string[];
    update: ActivityUpdate | null;
    children: ActivityNode[];
}

export interface BaselineSummary {
    id: number;
    workstream_id: number;
    name: string;
    reason: string | null;
    created_at: string | null;
}

export interface Workstream {
    id: number;
    project_id: number;
    name: string;
    owner_name: string | null;
    owner_user_id: number | null;
    order: number;
    status_note: string | null;
    phases: ActivityNode[];
    progress_real: number;
    progress_expected: number | null;
    planned_start: string | null;
    planned_end: string | null;
    baselines: BaselineSummary[];
}

export interface ExecutivePhaseCell {
    progress_real: number;
    progress_expected: number | null;
    status: ActivityStatus;
    planned_start: string | null;
    planned_end: string | null;
}

export interface ExecutiveRow {
    workstream_id: number;
    name: string;
    owner_name: string | null;
    status_note: string | null;
    phases: Record<string, ExecutivePhaseCell>;
    planned_start: string | null;
    planned_end: string | null;
    open_risks: number;
}

export interface Risk {
    id: number;
    project_id: number;
    workstream_id: number | null;
    workstream_name: string | null;
    kind: RiskKind;
    code: string;
    scope_label: string | null;
    description: string;
    impact_on: string | null;
    criticality: RiskCriticality;
    owner_name: string | null;
    status: RiskStatus;
    identified_date: string | null;
    target_dates: string[];
    mitigation: string | null;
    closed_date: string | null;
    activity_ids: number[];
}

export interface AccessItem {
    id: number;
    project_id: number;
    name: string;
    order: number;
}

export interface ProjectMember {
    id: number;
    project_id: number;
    user_id: number | null;
    allocation_id: number | null;
    name: string;
    squad: string | null;
    squad_lead: string | null;
    client_user_code: string | null;
    hostname: string | null;
    access_notes: string | null;
    access_owner: string | null;
    order: number;
    access: Record<string, AccessStatus>;
    access_complete?: boolean;
}

export interface ExecutionOverview {
    project: { id: number; name: string; status: string | null; client_name: string | null };
    report_date: string;
    workstreams: Workstream[];
    executive_view: { phase_names: string[]; rows: ExecutiveRow[] };
    risks: Risk[];
    issues: Risk[];
    access: { items: AccessItem[]; members: ProjectMember[]; pending_count: number };
}

export interface StatusReport {
    id: number;
    project_id: number;
    report_date: string;
    title: string;
    created_by: number | null;
    created_at: string | null;
}

// ------------------------------------------------------------ tarefas (Daily)
export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'BLOCKED' | 'DONE';
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH';

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
    TODO: 'A fazer', IN_PROGRESS: 'Em andamento', BLOCKED: 'Bloqueada', DONE: 'Concluída',
};
export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = { LOW: 'Baixa', MEDIUM: 'Média', HIGH: 'Alta' };

export interface ExecutionTask {
    id: number;
    project_id: number;
    workstream_id: number | null;
    workstream_name: string | null;
    activity_id: number | null;
    activity_name: string | null;
    member_id: number | null;
    assignee_name: string | null;
    squad: string | null;
    title: string;
    description: string | null;
    status: TaskStatus;
    priority: TaskPriority;
    due_date: string | null;
    completed_at: string | null;
    created_by: number | null;
    created_at: string;
    updated_at: string;
}

// ------------------------------------------------- importação da planilha
export interface ImportSheetPreview {
    title: string;
    kind: 'schedule' | 'text' | 'daily' | 'executive' | 'unknown';
    owner: string | null;
    report_date: string | null;
    phases: string[];
    activities: number;
    notes: number;
    tasks: number;
    warnings: string[];
    suggested: boolean;
}

export interface ImportPreview {
    sheets: ImportSheetPreview[];
}

export interface ImportResult {
    workstreams_created: number;
    workstreams_updated: number;
    activities_created: number;
    activities_updated: number;
    tasks_created: number;
    members_created: number;
    baselines: number;
    owners_not_found?: string[];
    people_not_found?: string[];
}
